/**
 * TourRunner — drives a guided tour over the real screens.
 *
 * Ported from the ECM host's TourRunner. Tours cross pages, so steps are driven
 * one at a time: before each step we navigate if needed, click what opens the
 * step's panel, wait for the anchor to render, then highlight it. A missing
 * anchor (a feature this account does not have, an empty list) degrades to a
 * centred popover with a note instead of stopping the tour.
 */
import { useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { driver, type Driver, type Side } from "driver.js";
import "driver.js/dist/driver.css";
import { GlobalStyles, useTheme } from "@mui/material";
import { post } from "@/api/client";
import { invalidate } from "@/lib/query";
import {
  START_TOUR_EVENT, TOUR_FINISHED_EVENT, escapeHtml, renderTourBody, samePlace, waitFor,
  type TourDefinition, type TourFinishedDetail, type TourStep,
} from "./tour";

const WAIT_MS = 5000;
const SIDES: Side[] = ["top", "right", "bottom", "left"];

export default function TourRunner() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const locRef = useRef(location);
  useEffect(() => { locRef.current = location; }, [location]);

  const drvRef = useRef<Driver | null>(null);
  const showRef = useRef<(index: number) => Promise<void>>(async () => {});
  const runRef = useRef<{ tour: TourDefinition; index: number; skipped: number } | null>(null);
  const tokenRef = useRef(0);

  const finish = useCallback((completed: boolean) => {
    const run = runRef.current;
    runRef.current = null;
    tokenRef.current += 1;
    drvRef.current?.destroy();
    drvRef.current = null;
    if (!run) return;
    if (completed && run.tour.track !== false) {
      post(`/training/tours/${encodeURIComponent(run.tour.id)}/complete`)
        .then(() => invalidate("/training"))
        .catch(() => { /* progress is best-effort; the tour itself finished */ });
    }
    window.dispatchEvent(new CustomEvent<TourFinishedDetail>(TOUR_FINISHED_EVENT, {
      detail: { id: run.tour.id, completed, skippedSteps: run.skipped },
    }));
  }, []);

  const show = useCallback(async (index: number) => {
    const run = runRef.current;
    if (!run) return;
    const token = ++tokenRef.current;
    run.index = index;
    const cancelled = () => tokenRef.current !== token;
    const step: TourStep = run.tour.steps[index];
    const total = run.tour.steps.length;

    drvRef.current?.destroy();
    drvRef.current = null;

    if (step.route && !samePlace(step.route, locRef.current)) navigate(step.route);
    if (step.click_before) {
      const trigger = await waitFor(step.click_before, WAIT_MS, cancelled);
      if (cancelled()) return;
      (trigger as HTMLElement | null)?.click();
    }

    let element: Element | null = null;
    let missing = false;
    if (step.target) {
      element = await waitFor(step.target, WAIT_MS, cancelled);
      if (cancelled()) return;
      missing = !element;
      if (missing) run.skipped += 1;
      element?.scrollIntoView({ block: "center", behavior: "instant" as ScrollBehavior });
    }

    const isLast = index === total - 1;
    const progressText = `${index + 1} of ${total}`;
    const note = missing
      ? `<p class="console-tour-missing">This part of the screen isn't on your page right now — there may be nothing here yet, or your account does not have it.</p>`
      : "";
    const side = SIDES.includes(step.placement as Side) ? (step.placement as Side) : "bottom";
    const leave = (next: number | "done" | "close") => {
      if (next === "done") finish(true);
      else if (next === "close") finish(false);
      else void showRef.current(next);
    };

    const d = driver({
      animate: true,
      allowClose: true,
      overlayOpacity: 0.55,
      stagePadding: 6,
      stageRadius: 8,
      popoverClass: "console-tour-popover",
      showProgress: true,
      progressText,
      nextBtnText: "Next",
      prevBtnText: "Back",
      doneBtnText: "Finish",
      onNextClick: () => leave(isLast ? "done" : index + 1),
      onPrevClick: () => leave(Math.max(0, index - 1)),
      onCloseClick: () => leave("close"),
      onDestroyStarted: () => {
        if (runRef.current && tokenRef.current === token) leave("close");
      },
      onPopoverRender: (popover) => {
        popover.nextButton.innerText = isLast ? "Finish" : "Next";
        popover.nextButton.style.display = "inline-block";
        popover.nextButton.disabled = false;
        if (index > 0) {
          popover.previousButton.style.display = "inline-block";
          popover.previousButton.disabled = false;
        } else {
          popover.previousButton.style.display = "none";
        }
        popover.progress.style.display = "block";
        popover.progress.innerText = progressText;
      },
    });
    drvRef.current = d;
    d.highlight({
      element: element ?? undefined,
      popover: {
        title: escapeHtml(step.title || run.tour.title || ""),
        description: renderTourBody(step.body || "") + note,
        side,
        align: "start",
        showButtons: ["next", "previous", "close"],
      },
    });
  }, [finish, navigate]);
  useEffect(() => { showRef.current = show; }, [show]);

  useEffect(() => {
    const onStart = (e: Event) => {
      const tour = (e as CustomEvent<TourDefinition>).detail;
      if (!tour?.id || !Array.isArray(tour.steps) || tour.steps.length === 0) return;
      if (runRef.current) finish(false);
      runRef.current = { tour, index: 0, skipped: 0 };
      void show(0);
    };
    window.addEventListener(START_TOUR_EVENT, onStart);
    return () => window.removeEventListener(START_TOUR_EVENT, onStart);
  }, [finish, show]);

  // Signing out unmounts the shell and ends any running tour.
  useEffect(() => () => { drvRef.current?.destroy(); }, []);

  const paper = theme.palette.background.paper;
  return (
    <GlobalStyles
      styles={{
        ".driver-popover.console-tour-popover": {
          fontFamily: theme.typography.fontFamily,
          background: paper,
          color: theme.palette.text.primary,
          borderRadius: 10,
          maxWidth: 380,
          boxShadow: theme.shadows[16],
        },
        ".driver-popover.console-tour-popover .driver-popover-title": {
          fontSize: "1rem", fontWeight: 600, fontFamily: "inherit",
        },
        ".driver-popover.console-tour-popover .driver-popover-description": {
          fontSize: "0.875rem", lineHeight: 1.55, color: theme.palette.text.secondary,
          fontFamily: "inherit",
        },
        ".driver-popover.console-tour-popover .console-tour-missing": {
          marginTop: 8, fontSize: "0.78rem", fontStyle: "italic",
        },
        ".driver-popover.console-tour-popover .driver-popover-progress-text": {
          color: theme.palette.text.secondary, fontSize: "0.75rem",
        },
        ".driver-popover.console-tour-popover button": {
          fontFamily: "inherit", textShadow: "none", borderRadius: 6, padding: "4px 12px",
          fontSize: "0.8rem",
        },
        ".driver-popover.console-tour-popover .driver-popover-next-btn": {
          background: theme.palette.primary.main, color: theme.palette.primary.contrastText,
          border: "none",
        },
        ".driver-popover.console-tour-popover .driver-popover-close-btn": {
          color: theme.palette.text.secondary, padding: 0,
        },
        ".driver-popover.console-tour-popover .driver-popover-arrow-side-top.driver-popover-arrow": {
          borderTopColor: paper,
        },
        ".driver-popover.console-tour-popover .driver-popover-arrow-side-bottom.driver-popover-arrow": {
          borderBottomColor: paper,
        },
        ".driver-popover.console-tour-popover .driver-popover-arrow-side-left.driver-popover-arrow": {
          borderLeftColor: paper,
        },
        ".driver-popover.console-tour-popover .driver-popover-arrow-side-right.driver-popover-arrow": {
          borderRightColor: paper,
        },
      }}
    />
  );
}
